import { useEffect, useMemo, useRef, useState } from "react";
import { discontinuousTimeScaleProvider } from "@chartLib/lib/scale";
import { last } from "@chartLib/lib/utils";
import {
  runCalculator,
  applyIndicators,
  AGGREGATION_TYPES,
} from "../utils/calculators";
import { applyBarColors } from "../utils/barColorProcessor";
import { patchDiscontinuousProvider } from "./providerPatch";
import { useSettingsStore } from "../../../store/settingsStore";
import { getLocale } from "../../../utils/locales";
import { getLocaleFormatters } from "../../../hooks/useChartLocale";
import performanceMonitor from "../../../utils/performanceMonitor.js";
import { mergeAllComparisons } from "../../../store/compare";
import { useChartsStore } from "../../../store/chartStore";
import {
  isRemount as isRemountByKey,
  keyChangedSince,
  resolveStoredXExtents,
} from "./xExtentsKeyDecision.js";
import { createDataHash } from "./dataHash.js";
import { scaleCacheKey } from "./scaleCacheKey.js";

// Apply the memory leak patch
const cleanupPatch = patchDiscontinuousProvider();

// Cleanup patch on module unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", cleanupPatch);
}

// Create a stable date accessor outside the component to prevent recreations
const STABLE_DATE_ACCESSOR = (d) => d.date;

// Global cache with size limit to prevent unlimited growth
class ProviderCache {
  constructor(maxSize = 50) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key) {
    const value = this.cache.get(key);
    if (value) {
      // Move to end (LRU)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  clear() {
    this.cache.clear();
  }
}

const globalProviderCache = new ProviderCache(20); // Reduced from 50 for more frequent cache eviction

// setTimeout and requestAnimationFrame ids come from separate browser counters that
// overlap numerically: handing one kind to the other's cancel function cancels an
// unrelated timer/frame that happens to share the number.
function cancelPending(timeoutRef, rafRef) {
  if (timeoutRef.current) clearTimeout(timeoutRef.current);
  if (rafRef.current) cancelAnimationFrame(rafRef.current);
  timeoutRef.current = null;
  rafRef.current = null;
}

export const useChartData = ({
  chartId = null,
  initialData,
  dataVersion = 0,
  chartType,
  indicators,
  barColorProvider = "traditional",
  barColorConfig = {},
  renkoConfig,
  pointFigureConfig,
  periodicity,
  pendingInvalidation = false,
  visibleBars: storedVisibleBars = 100,
  storedXExtents = null,
  resetTriggered = false,
  onVisibleBarsChange,
  perfMonitor,
  comparisonSymbols = [],
  isCompareMode = false,
}) => {
  const prevKeyRef = useRef({ periodicity: null, chartType: null });
  const prevIndicatorsRef = useRef(null);
  const mountedRef = useRef(true);
  const timeoutRef = useRef(null);
  const rafRef = useRef(null);
  const frameRequestedRef = useRef(false);
  const invalidationHandledRef = useRef(false);
  const clearInvalidation = useChartsStore((state) => state.clearInvalidation);
  const lastUpdateRef = useRef(0);

  // Get current locale from settings
  const currentLocale = useSettingsStore((state) => state.locale);
  const showIchimokuFutureProjection = useSettingsStore(
    (state) => state.showIchimokuFutureProjection ?? true,
  );

  const [state, setState] = useState({
    xScale: null,
    xAccessor: null,
    displayXAccessor: null,
    data: null,
    xExtents: null,
  });

  // Force cache invalidation when indicators change significantly
  useEffect(() => {
    const currentIndicatorsKey = indicators
      .map((i) => `${i.id}-${i.renderKey}`)
      .join(",");
    const prevIndicatorsKey = prevIndicatorsRef.current;

    if (prevIndicatorsKey && prevIndicatorsKey !== currentIndicatorsKey) {
      // Clear both caches when indicators change
      globalProviderCache.clear();
      // Force a complete re-render by clearing state data
      setState((prev) => ({ ...prev, data: null }));
    }

    prevIndicatorsRef.current = currentIndicatorsKey;
  }, [indicators]);

  // Track previous compare mode to detect changes
  const prevCompareModeRef = useRef(isCompareMode);

  // Force cache invalidation and Y-axis reset when compare mode changes
  useEffect(() => {
    const wasCompareMode = prevCompareModeRef.current;

    if (wasCompareMode !== isCompareMode) {
      // Clear caches to force recalculation with/without comparison data
      globalProviderCache.clear();
      // Force a complete re-render by clearing state data
      setState((prev) => ({ ...prev, data: null }));
    }

    prevCompareModeRef.current = isCompareMode;
  }, [isCompareMode]);

  // Throttled data processing to prevent excessive calculations
  const processedData = useMemo(() => {
    // Guard: Skip processing if no data (race condition during workspace restore)
    if (!initialData || initialData.length === 0) {
      return null;
    }

    // Track data update in performance monitor
    perfMonitor?.trackArrayAllocation();

    // Include dataVersion in hash to ensure cache invalidation on in-place mutations
    const dataHash = createDataHash(
      initialData,
      chartType,
      indicators,
      renkoConfig,
      pointFigureConfig,
      dataVersion,
      comparisonSymbols,
      periodicity,
      barColorProvider,
      barColorConfig,
    );

    // When pendingInvalidation is set (fresh historical data from setData/fetchChartData),
    // skip cache entirely and clear it — forces full reprocessing with the new data.
    if (pendingInvalidation) {
      globalProviderCache.clear();
    } else {
      // Check cache first (for quote-driven updates)
      const cached = globalProviderCache.get(dataHash);
      if (cached) {
        return cached;
      }
    }

    // Process data immediately for real-time updates
    // Pass the appropriate config based on chart type
    const chartTypeConfig =
      chartType === "pointfigure" ? pointFigureConfig : renkoConfig;
    const baseData = runCalculator(chartType, initialData, chartTypeConfig);

    const dataWithIndicators = applyIndicators(baseData, indicators);

    // Track indicator calculation for performance monitoring
    performanceMonitor.trackIndicatorCalculation();

    // Merge comparison data if in compare mode
    // Filter out symbols that are still loading or have errors
    const validComparisons =
      comparisonSymbols?.filter(
        (s) => s.data?.length > 0 && !s.isLoading && !s.error,
      ) || [];

    const dataWithComparisons =
      validComparisons.length > 0
        ? mergeAllComparisons(dataWithIndicators, validComparisons)
        : dataWithIndicators;

    // Apply bar colors after indicators and comparisons
    const finalData = applyBarColors(
      dataWithComparisons,
      barColorProvider,
      barColorConfig,
      indicators,
    );

    if (!finalData || finalData.length === 0) {
      return null;
    }

    // Limit data size more aggressively
    const MAX_DATA_POINTS = 16000; // Increased from 5000 for more historical data
    const result =
      finalData.length > MAX_DATA_POINTS
        ? finalData.slice(-MAX_DATA_POINTS)
        : finalData;

    // Cache the result for future lookups
    globalProviderCache.set(dataHash, result);

    // DevTools tap (issue debug): when capture is armed via devTools.exportChartData,
    // stash the fully-processed plotted rows (OHLC + every indicator/comparison/bar-color
    // output) keyed by chartId so the export reads EXACTLY what is rendered. Gated behind a
    // window flag -> a single short-circuiting boolean read when disarmed (no allocation,
    // no retained reference, no extra render), and never on the draw/render hot path since
    // this memo only recomputes when chart data/config actually changes.
    if (
      typeof window !== "undefined" &&
      window.__CAPTURE_PLOTTED__ &&
      chartId != null
    ) {
      (window.__PLOTTED__ ||= {})[chartId] = result;
    }

    return result;
  }, [
    initialData,
    dataVersion,
    chartType,
    indicators,
    barColorProvider,
    barColorConfig,
    renkoConfig,
    pointFigureConfig,
    comparisonSymbols,
    isCompareMode,
    periodicity,
  ]); // periodicity ensures cache key differentiation between periodicities

  // #432: agregação (Renko/P&F/…) que não produziu NENHUMA linha, embora existam
  // barras cruas. `processedData` vira null e o efeito de estado abaixo aborta —
  // ou seja, o chart continua exibindo o resultado ANTERIOR (ou nada, se nunca
  // houve um). Sem este sinal a UI não tem como distinguir isso de "carregando",
  // e o usuário fica com a tela em branco sem explicação.
  const aggregationEmpty = useMemo(
    () =>
      processedData === null &&
      initialData?.length > 0 &&
      AGGREGATION_TYPES.includes(chartType),
    [processedData, initialData, chartType],
  );

  // Cache for scale provider results using Map with size limit for better control
  const scaleCache = useMemo(() => {
    return new Map();
  }, [indicators, isCompareMode]); // Recreate cache when indicators or compare mode change

  // Create or reuse scale provider with caching that's sensitive to real-time updates
  const dataAndScale = useMemo(() => {
    if (!processedData) return null;

    // #440: identity of processedData, never a fingerprint — see scaleCacheKey.js.
    const cacheKey = scaleCacheKey(processedData);

    // When pendingInvalidation is set, clear scale cache to force recomputation
    if (pendingInvalidation) {
      scaleCache.clear();
    } else if (scaleCache.has(cacheKey)) {
      return scaleCache.get(cacheKey);
    }

    // Clear cache if it gets too large (prevent memory leak)
    if (scaleCache.size > 3) {
      // Further reduced cache size for more frequent updates when indicators change
      scaleCache.clear();
    }

    // Create provider with minimal allocations and error handling
    try {
      // Configure provider with locale and formatters
      const localeDefinition = getLocale(currentLocale);
      const formatters = getLocaleFormatters();

      const provider = discontinuousTimeScaleProvider
        .inputDateAccessor(STABLE_DATE_ACCESSOR)
        .setLocale(localeDefinition, formatters);

      const result = provider(processedData);

      // Validate the result structure before caching
      if (
        result &&
        result.data &&
        result.xAccessor &&
        result.xScale &&
        result.displayXAccessor
      ) {
        // Cache the scale result for future lookups
        scaleCache.set(cacheKey, result);
        return result;
      } else {
        console.warn("Invalid provider result structure:", result);
        return null;
      }
    } catch (error) {
      console.warn("Provider error:", error);
      return null;
    }
  }, [processedData, scaleCache, indicators, currentLocale, comparisonSymbols]); // Added currentLocale and comparisonSymbols dependency

  // Throttled state update to reduce render frequency, especially during mouse interactions
  useEffect(() => {
    if (!dataAndScale) {
      return;
    }

    // Cancel any pending updates to prevent stale closures from firing
    if (timeoutRef.current || rafRef.current) {
      cancelPending(timeoutRef, rafRef);
      frameRequestedRef.current = false; // Fix: reset so next rAF isn't permanently blocked
    }

    // Fresh historical data (from setData/fetchChartData) bypasses throttle entirely.
    // Under heavy real-time quote load, the rAF-scheduled updates get starved because
    // each quote tick re-enters this effect and cancels the pending rAF before it fires.
    if (pendingInvalidation && !invalidationHandledRef.current) {
      console.log(
        `[useChartData] INVALIDATE chartId=${chartId} periodicity=${periodicity} dataLen=${dataAndScale?.data?.length}`,
      );
      invalidationHandledRef.current = true;
      lastUpdateRef.current = performance.now();
      updateState();
      if (chartId) clearInvalidation(chartId);
      return;
    }
    if (!pendingInvalidation) {
      invalidationHandledRef.current = false;
    }

    // Throttle updates to max 30fps (33ms) to prevent excessive re-renders during mouse interactions
    const now = performance.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;
    const minInterval = 33; // ~30fps

    if (timeSinceLastUpdate < minInterval && !frameRequestedRef.current) {
      // Too soon - schedule for later (with fresh closure)
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        if (mountedRef.current) {
          lastUpdateRef.current = performance.now();
          updateStateWithAnimation();
        }
      }, minInterval - timeSinceLastUpdate);
      return;
    }

    // Update immediately if enough time has passed
    lastUpdateRef.current = now;
    updateStateWithAnimation();

    function updateStateWithAnimation() {
      // Use requestAnimationFrame with debouncing to prevent excessive frame requests
      if (!frameRequestedRef.current) {
        frameRequestedRef.current = true;
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          frameRequestedRef.current = false;
          // Only call updateState if component is still mounted
          if (mountedRef.current) {
            updateState();
          }
        });
      }
    }

    function updateState() {
      // Prevent state updates if component has unmounted
      if (!mountedRef.current) return;

      try {
        const keyChanged = keyChangedSince(
          prevKeyRef.current,
          periodicity,
          chartType,
        );

        const { data, xAccessor, xScale, displayXAccessor } = dataAndScale;

        // Validate data structure before processing
        if (
          !data ||
          !Array.isArray(data) ||
          data.length === 0 ||
          !xAccessor ||
          !xScale
        ) {
          console.warn("Invalid dataAndScale structure:", dataAndScale);
          return;
        }

        const dataLen = data.length;

        // Always use the stored visible bars (which defaults to 100 for new charts)
        // but clamp to available data
        const requestedVisibleBars = storedVisibleBars;
        const maxVisibleBars = Math.max(1, dataLen); // At least 1, but not more than available data
        const actualVisibleBars = Math.min(
          requestedVisibleBars,
          maxVisibleBars,
        );

        // If we had to adjust visible bars due to insufficient data, update the store
        // This ensures we save the "nearest possible value" when data is limited
        if (actualVisibleBars !== requestedVisibleBars && onVisibleBarsChange) {
          onVisibleBarsChange(actualVisibleBars);
        }

        const start = xAccessor(data[Math.max(0, dataLen - actualVisibleBars)]);
        const lastRealEnd = xAccessor(last(data));

        // Issue #161 — Ichimoku Future Projection (Fase 2 + Fase 3 flag):
        // When any Ichimoku indicator is active AND the feature flag is on, extend
        // the right edge of xExtents by its `displacement` (default 26) so the
        // projected future cloud area is visible at default load. Without this,
        // CANVAS_PADDING.right=15px clips most of the 26 projected bars.
        // This only modifies the viewport; chartState.data is untouched.
        const ichimokuIndicators =
          showIchimokuFutureProjection && indicators
            ? indicators.filter(
                (ind) =>
                  ind &&
                  (ind.renderKey === "ichimoku" || ind.key === "ichimoku"),
              )
            : [];
        const futureProjectionExtension = ichimokuIndicators.reduce(
          (max, ind) => {
            const disp = ind && ind.config && Number(ind.config.displacement);
            return Number.isFinite(disp) && disp > max ? disp : max;
          },
          0,
        );
        const end = lastRealEnd + futureProjectionExtension;

        // Only reset xExtents when key actually changed (periodicity/chartType)
        // Otherwise, preserve existing xExtents to maintain user's zoom/pan state
        let xExtents;
        // Distinguish genuine key change from component remount (Issue #254).
        // On remount (fitWidth destructive resize cycle, FlexLayout panel rearrangement),
        // prevKeyRef starts as {null, null} which falsely triggers keyChanged.
        // If storedXExtents exist in the store, they're still valid for the same data.
        const isRemount = isRemountByKey(prevKeyRef.current);
        if (resetTriggered) {
          // Local chart reset should rebuild the default viewport for this chart only.
          // Do not preserve prior xExtents/state.xExtents when reset is active.
          xExtents = [start, end];
        } else if (keyChanged && !isRemount) {
          // Genuine key change (periodicity or chartType changed by user):
          // ALWAYS recalculate extents from new data.
          // storedXExtents are from old data format and may reference invalid indices
          // (e.g., Renko has different indices than candle data)
          xExtents = [start, end];
        } else if (storedXExtents) {
          // Validate a stored viewport against the current data bounds before
          // reusing it: clamp on history-limit overshoot, re-extend for the
          // Ichimoku projection, else preserve a deliberate pan/zoom. Pure
          // decision lives in xExtentsKeyDecision (#360 / #161 / #168 / #163).
          const maxIndex = xAccessor(last(data));
          const minIndex = xAccessor(data[0]);
          xExtents = resolveStoredXExtents(storedXExtents, {
            minIndex,
            maxIndex,
            start,
            end,
            futureProjectionExtension,
          });
        } else if (state.xExtents) {
          // No stored extents but we have existing extents: preserve them
          xExtents = state.xExtents;
        } else {
          // Fallback: use calculated extents from visible bars
          xExtents = [start, end];
        }

        // Notify about xExtents changes if callback is provided
        // Removed onXExtentsChange to prevent feedback loops during resize

        prevKeyRef.current = { periodicity, chartType };

        // Direct setState — the throttle (setTimeout + rAF) already rate-limits updates.
        // startTransition was causing ALL data updates (historical load, symbol change,
        // periodicity change) to be indefinitely deferred under continuous real-time quote load,
        // because React 18 treats quotes as high-priority work that starves low-priority transitions.
        if (mountedRef.current) {
          setState({
            data,
            xAccessor,
            xScale,
            displayXAccessor,
            xExtents,
          });
        }
      } catch (error) {
        console.error("Error updating chart state:", error);
      }
    }

    return () => {
      cancelPending(timeoutRef, rafRef);
      frameRequestedRef.current = false;
    };
  }, [
    dataAndScale,
    periodicity,
    chartType,
    storedVisibleBars,
    storedXExtents,
    resetTriggered,
    onVisibleBarsChange,
    pendingInvalidation,
    indicators,
    showIchimokuFutureProjection,
  ]); // pendingInvalidation triggers immediate render for fresh data; indicators + Ichimoku flag drive xExtents extension

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    // Schedule cache cleanup to prevent memory buildup
    const cleanupTimeoutId = setTimeout(() => {
      globalProviderCache.clear();
    }, 5000); // Delayed cleanup

    return () => {
      // Clear references when component unmounts
      prevKeyRef.current = { periodicity: null, chartType: null };

      // Mark component as unmounted to prevent state updates
      mountedRef.current = false;

      // Clear any pending timeout or animation frame
      cancelPending(timeoutRef, rafRef);
      frameRequestedRef.current = false;

      // Clear the cleanup timeout to prevent it from executing after unmount
      clearTimeout(cleanupTimeoutId);
    };
  }, []);

  return aggregationEmpty ? { ...state, aggregationEmpty } : state;
};
